import * as Sentry from '@sentry/react';
import { useCallback, useState } from 'react';

import { type ChatAttachment } from '@/components/agents/chat-attachments';
import { useNotification } from '@/components/notifications';
import { useUploadAgentChatFile } from '@/hooks/mutations/use-upload-agent-chat-file';
import { AGENT_FILE_MAX_BYTES } from '@/services/agents/files';

export type UploadedChatAttachment = ChatAttachment & { fileId: string };

/**
 * Upload state for a chat composer: every attachment shows up immediately and
 * flips to uploaded/error as its request settles. Shared by the chat surface
 * composer and the Chats page prompt bar so both attach files identically.
 */
export function useChatAttachmentUploads(orgId?: string, agentId?: string) {
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const notification = useNotification();
  const { mutate: uploadAgentFile } = useUploadAgentChatFile();

  const addFiles = useCallback(
    (files: File[]) => {
      if (!orgId || !agentId) return;
      for (const file of files) {
        if (file.size > AGENT_FILE_MAX_BYTES) {
          notification.error(
            `${file.name} is larger than the ${Math.floor(AGENT_FILE_MAX_BYTES / 1024 / 1024)} MB limit`,
          );
          continue;
        }
        const localId = `att-${crypto.randomUUID()}`;
        setAttachments((prev) => [
          ...prev,
          {
            localId,
            filename: file.name,
            sizeBytes: file.size,
            status: 'uploading',
          },
        ]);
        uploadAgentFile(
          { orgId, agentId, file },
          {
            onSuccess: (uploaded) => {
              setAttachments((prev) =>
                prev.map((a) =>
                  a.localId === localId
                    ? { ...a, status: 'uploaded', fileId: uploaded.fileId }
                    : a,
                ),
              );
            },
            onError: (err) => {
              const message =
                err instanceof Error ? err.message : 'Upload failed';
              setAttachments((prev) =>
                prev.map((a) =>
                  a.localId === localId
                    ? { ...a, status: 'error', error: message }
                    : a,
                ),
              );
              notification.error(`Failed to upload ${file.name}: ${message}`);
              Sentry.captureException(err, {
                extra: {
                  context: 'chat-attachment-upload',
                  orgId,
                  agentId,
                  filename: file.name,
                  sizeBytes: file.size,
                },
              });
            },
          },
        );
      }
    },
    [orgId, agentId, notification, uploadAgentFile],
  );

  const removeAttachment = useCallback((localId: string) => {
    setAttachments((prev) => prev.filter((a) => a.localId !== localId));
  }, []);

  const clearAttachments = useCallback(() => setAttachments([]), []);

  const uploadedAttachments = attachments.filter(
    (a): a is UploadedChatAttachment => a.status === 'uploaded' && !!a.fileId,
  );
  const isUploading = attachments.some((a) => a.status === 'uploading');

  return {
    attachments,
    addFiles,
    removeAttachment,
    clearAttachments,
    uploadedAttachments,
    isUploading,
  };
}
